import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSpinner,
  faIdCard,
  faUser,
  faEnvelope,
  faPhone,
  faLocationDot,
  faBuildingColumns,
  faCreditCard,
  faFileLines,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { Row, Col, Card, Button, Badge, Table } from "@themesberg/react-bootstrap";
import { getSingleUserById, adminGetUserSubIds } from "../../api";
import { MEDIA_URL } from "../../helper";
import { Routes } from "../../../routes";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const getStatusBadgeStyle = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "active":
      return {
        background: "#10b981",
        color: "#ffffff",
      };
    case "inactive":
      return {
        background: "#f59e0b",
        color: "#ffffff",
      };
    case "blocked":
      return {
        background: "#ef4444",
        color: "#ffffff",
      };
    default:
      return {
        background: "#6b7280",
        color: "#ffffff",
      };
  }
};

const capitalize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());


const KYC_STATUS_VARIANT = (status) => {
  const key = String(status || "").toUpperCase();

  const MAP = {
    VERIFIED: "success",
    DONE: "success",
    PENDING: "warning",
    REJECTED: "danger",
  };

  return MAP[key] || "secondary";
};

const normalizeUser = (item) => ({
  id: item?.id ?? "N/A",
  name: item?.name ?? "N/A",
  email: item?.email ?? "N/A",
  mobile: item?.mobile_number ?? "N/A",
  userType: item?.user_type ?? "N/A",
  status: item?.status ?? "N/A",
  kycStatus: capitalize(item?.kyc_status) ?? "N/A",
  profilePhoto: item?.profile_photo ?? "N/A",
  referralNumber: item?.referral_number ?? "N/A",
  customUserId: item?.custom_user_id ?? "N/A",
  aadhaarNumber: item?.aadhar_number ?? "N/A",
  panNumber: item?.pan_number ?? "N/A",
  addressLine1: item?.address_line_1 ?? "N/A",
  addressLine2: item?.address_line_2 ?? "N/A",
  state: item?.state ?? "N/A",
  city: item?.city ?? "N/A",
  pincode: item?.pincode ?? "N/A",
  // idProof: item?.id_proof || null,
  bankName: item?.bank_details?.bank_name ?? "N/A",
  accountHolderName: item?.bank_details?.account_holder_name ?? "N/A",
  accountNumber: item?.bank_details?.account_number ?? "N/A",
  ifscCode: item?.bank_details?.ifsc_code ?? "N/A",
  totalEarnings: item?.total_earning ?? "0",
  level: item?.level ?? "N/A",
  walletBalance: item?.wallet_balance ?? "0",
  // businessGenerated: item?.business_generated ?? "0",
});


const normalizeFileUrl = (value) => {
  if (!value) return "";
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  return `${MEDIA_URL}${value.startsWith("/") ? value : `/${value}`}`;
};

const formatStatus = (value) => {
  if (!value) return "N/A" || "-";
  const str = String(value).trim();
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const isImageFile = (value) => /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(String(value || ""));

const DetailBox = ({ label, value }) => (
  <div className="detail-box">
    <span className="text-muted small">{label}</span>
    <div className="fw-semibold text-break">{value || "N/A" || "-"}</div>
  </div>
);

const MediaPreview = ({ title, filePath, fallbackText, icon, onView }) => {
  const resolved = normalizeFileUrl(filePath);
  const hasFile = Boolean(String(filePath || "").trim());
  const showImage = hasFile && isImageFile(filePath);

  return (
    <div className="media-preview-card">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div className="fw-semibold">{title}</div>
          <div className="text-muted small">{fallbackText || "Uploaded document"}</div>
        </div>
        <Badge bg={hasFile ? "light" : "warning"} text={hasFile ? "dark" : "white"} className="px-3 py-2 rounded-pill">
          {hasFile ? "Available" : "Missing"}
        </Badge>
      </div>

      {hasFile ? (
        <>
          <div className="media-preview-thumb">
            {showImage ? (
              <img
                src={resolved}
                alt={title}
                className="media-preview-image"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className="media-preview-file">
                <FontAwesomeIcon icon={icon} />
              </div>
            )}
          </div>

          <div className="d-flex justify-content-center align-items-center mt-3 gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              onClick={onView}
              className="view-btn"
            >
              <FontAwesomeIcon icon={faEye} className="me-2" />
              View
            </Button>
          </div>
        </>
      ) : (
        <div className="media-empty">
          <div className="media-preview-file">
            <FontAwesomeIcon icon={icon} />
          </div>
          <div className="text-muted small mt-3">No {title.toLowerCase()} uploaded</div>
        </div>
      )}
    </div>
  );
};

export default function AdminUserDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [subIds, setSubIds] = useState([]);
  const [loadingSubIds, setLoadingSubIds] = useState(false);

  const getAdminEditUserPath = (userId) => {
    return Routes?.AdminEditUser?.path?.replace(":id", userId) || `/admin-edit-user/${userId}`;
  };

  const getAdminUserTransactionHistoryPath = (userId) => {
    return Routes?.AdminUserTransactionHistory?.path?.replace(":id", userId) || `/admin-user-transactions/${userId}`;
  };

  const getAdminUserCoursesPath = (userId) => {
    return Routes?.AdminUserCourseList?.path?.replace(":id", userId) || `/admin-user-courses/${userId}`;
  };

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const res = await getSingleUserById(id);
        const apiUser = res?.data?.data;
        setUser(apiUser ? normalizeUser(apiUser) : null);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to fetch user details.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchUser();
  }, [id]);

  useEffect(() => {
    const fetchSubIds = async () => {
      if (!id) return;
      try {
        setLoadingSubIds(true);
        const res = await adminGetUserSubIds(id);
        setSubIds(res?.data?.data?.sub_ids || []);
      } catch {
        setSubIds([]);
      } finally {
        setLoadingSubIds(false);
      }
    };

    fetchSubIds();
  }, [id]);

  return (
    <>
      <div className="admin-user-header py-4">
        <div className="admin-user-header-title">
          {/* <h5 className="mb-0">User Details</h5> */}
          <h2 className="h4 mb-1">User Details</h2>
        </div>

        <div className="admin-user-header-actions">

          {/* <Button
            type="button"
            onClick={() => navigate(getAdminUserTransactionHistoryPath(id))}
            style={ACTIVE_BUTTON_STYLE}
          >
            View Transactions
          </Button> */}

          <Button
            type="button"
            onClick={() => navigate(getAdminEditUserPath(id))}
            style={ACTIVE_BUTTON_STYLE}
          >
            Edit User
          </Button>

          <Button type="button" variant="outline-primary" onClick={() => navigate(-1)}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-4 user-detail-shell">
        <Card.Body className="p-2">
          {loading ? (
            <div className="text-center text-muted py-5">
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
              Loading user details...
            </div>
          ) : user ? (
            <Row className="g-4">
              <Col xs={12} lg={4}>
                <Card className="border-0 shadow-sm rounded-4 h-100 profile-summary-card">
                  <Card.Body className="p-4">
                    <div className="profile-top">
                      <div className="profile-avatar-wrap">
                        {user.profilePhoto ? (
                          <img
                            src={normalizeFileUrl(user.profilePhoto)}
                            alt={user.name || "Profile"}
                            className="profile-avatar"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                              const fallback =
                                e.currentTarget.parentElement?.querySelector(".profile-avatar-fallback");
                              if (fallback) fallback.style.display = "flex";
                            }}
                          />
                        ) : null}

                        <div
                          className="profile-avatar-fallback"
                          style={{ display: user.profilePhoto ? "none" : "flex" }}
                        >
                          <FontAwesomeIcon icon={faUser} />
                        </div>
                      </div>

                      <h4 className="mb-1 mt-3">{user.name || "N/A" || "-"}</h4>
                      <div className="text-muted mb-2 text-break">{user.email || "N/A" || "-"}</div>

                      <div className="profile-mini-item mb-1 d-flex justify-content-between align-items-center gap-2">
                        <span className="text-muted small">Mobile</span>
                        <div className="fw-semibold text-end"> {user.mobile || "N/A" || "-"}</div>
                      </div>

                      <div className="profile-mini-item mb-1 d-flex justify-content-between align-items-center gap-2">
                        <span className="text-muted small">User Id</span>
                        <div className="fw-semibold text-end"> {user.customUserId || "N/A" || "-"}</div>
                      </div>

                      <div className="profile-mini-item mb-1 d-flex justify-content-between align-items-center gap-2">
                        <span className="text-muted small">Referral No.</span>
                        <div className="fw-semibold text-end"> {user.referralNumber || "N/A" || "-"}</div>
                      </div>

                      {/* <div className="profile-mini-item mb-1 d-flex justify-content-between align-items-center gap-2">
                        <span className="text-muted small">Business Generated</span>
                        <div className="fw-semibold text-end"> ₹{user.businessGenerated || "0"}</div>
                      </div> */}

                      <div className="profile-mini-item mb-1 d-flex justify-content-between align-items-center gap-2">
                        <span className="text-muted small">Total Earnings</span>
                        <div className="fw-semibold text-end"> ₹{user.totalEarnings || "0"}</div>
                      </div>

                      <div className="profile-mini-item mb-1 d-flex justify-content-between align-items-center gap-2">
                        <span className="text-muted small">Wallet Balance</span>
                        <div className="fw-semibold text-end"> ₹{user.walletBalance || "0"}</div>
                      </div>

                      <div className="profile-mini-item mb-1 d-flex justify-content-between align-items-center gap-2">
                        <span className="text-muted small">User Level</span>
                        <div className="fw-semibold text-end"> {user.level || "N/A" || "-"}</div>
                      </div>

                      {/* <div className="profile-mini-item mb-1 d-flex justify-content-between align-items-center gap-2">
                        <Button
                          type="button"
                          onClick={() => navigate(getAdminUserTransactionHistoryPath(id))}
                          className="w-100 act-btn"
                        >
                          View Transactions
                        </Button>
                      </div>

                      <div className="profile-mini-item d-flex justify-content-between align-items-center gap-2">
                        <Button
                          type="button"
                          onClick={() => navigate(getAdminUserCoursesPath(id))}
                          className="w-100 act-btn"
                        >
                          View Courses
                        </Button>
                      </div> */}
                    </div>

                    <hr className="my-4" />

                    {/* <Col md={12}>
                      <MediaPreview
                        title="ID Proof"
                        filePath={user.idProof}
                        fallbackText="Uploaded identity document"
                        icon={faFileLines}
                        onView={() => {
                          const url = normalizeFileUrl(user.idProof);
                          if (url) window.open(url, "_blank", "noopener,noreferrer");
                        }}
                      />
                    </Col> */}
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} lg={8}>
                <Row className="g-4 mt-1">
                  <Col xs={12}>
                    <Card className="border-0 shadow-sm rounded-4">
                      <Card.Body className="p-2">
                        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                          <div>
                            <h5 className="mb-1">Profile Details</h5>
                            <div className="text-muted small">Basic account information</div>
                          </div>

                          <Badge
                            className="px-3 py-2 rounded-pill"
                            style={getStatusBadgeStyle(user.status)}
                          >
                            {formatStatus(user.status)}
                          </Badge>
                        </div>

                        <Row className="g-3">
                          <Col md={6}>
                            <DetailBox label="Full Name" value={user.name} />
                          </Col>
                          <Col md={6}>
                            <DetailBox label="Email" value={user.email} />
                          </Col>
                          <Col md={6}>
                            <DetailBox label="Mobile Number" value={user.mobile} />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12}>
                    <Card className="border-0 shadow-sm rounded-4">
                      <Card.Body className="p-2">
                        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                          <div>
                            <h5 className="mb-1">KYC / Identity Details</h5>
                            <div className="text-muted small">Identity and verification information</div>
                          </div>
                          {/* <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill">
                            KYC {user.kycStatus || "N/A" || "-"}
                          </Badge> */}

                          <Badge
                             bg={KYC_STATUS_VARIANT(user.kycStatus)}
                            className="px-3 py-2 rounded-pill"
                          >
                            KYC {user.kycStatus}
                          </Badge>
                        </div>

                        <Row className="g-3">
                          <Col md={6}>
                            <DetailBox label="Referral Number" value={user.referralNumber} />
                          </Col>
                          <Col md={6}>
                            <DetailBox label="KYC Status" value={user.kycStatus} />
                          </Col>
                          <Col md={6}>
                            <DetailBox label="PAN Number" value={user.panNumber} />
                          </Col>
                          <Col md={6}>
                            <DetailBox label="Aadhaar Number" value={user.aadhaarNumber} />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12}>
                    <Card className="border-0 shadow-sm rounded-4">
                      <Card.Body className="p-2">
                        <h5 className="mb-3">Address Details</h5>
                        <Row className="g-3">
                          <Col md={6}>
                            <DetailBox label="Address Line 1" value={user.addressLine1} />
                          </Col>
                          <Col md={6}>
                            <DetailBox label="Address Line 2" value={user.addressLine2} />
                          </Col>
                          <Col md={4}>
                            <DetailBox label="State" value={user.state} />
                          </Col>
                          <Col md={4}>
                            <DetailBox label="City" value={user.city} />
                          </Col>
                          <Col md={4}>
                            <DetailBox label="Pincode" value={user.pincode} />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12}>
                    <Card className="border-0 shadow-sm rounded-4">
                      <Card.Body className="p-2">
                        <h5 className="mb-3">Sub-IDs</h5>
                        {loadingSubIds ? (
                          <div className="text-muted py-3">Loading sub-IDs...</div>
                        ) : subIds.length > 0 ? (
                          <div className="table-responsive">
                            <Table hover className="align-middle mb-0">
                              <thead>
                                <tr>
                                  <th>Custom ID</th>
                                  <th>Username</th>
                                  <th>Name</th>
                                  <th>Status</th>
                                  <th>Earnings</th>
                                  <th>Created</th>
                                </tr>
                              </thead>
                              <tbody>
                                {subIds.map((item) => (
                                  <tr key={item.custom_user_id}>
                                    <td>{item.custom_user_id}</td>
                                    <td>{item.username || "-"}</td>
                                    <td>{item.name}</td>
                                    <td>{formatStatus(item.status)}</td>
                                    <td>₹{Number(item.total_earnings || 0).toLocaleString("en-IN")}</td>
                                    <td>{item.created_at || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-muted py-3">No sub-IDs owned by this user.</div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col xs={12}>
                    <Card className="border-0 shadow-sm rounded-4">
                      <Card.Body className="p-2">
                        <h5 className="mb-3">Bank Details</h5>
                        <Row className="g-3">
                          <Col md={6}>
                            <DetailBox label="Bank Name" value={user.bankName} />
                          </Col>
                          <Col md={6}>
                            <DetailBox label="Account Holder Name" value={user.accountHolderName} />
                          </Col>
                          <Col md={6}>
                            <DetailBox label="Account Number" value={user.accountNumber} />
                          </Col>
                          <Col md={6}>
                            <DetailBox label="IFSC Code" value={user.ifscCode} />
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>
          ) : (
            <div className="text-center text-muted py-5">No user details found.</div>
          )}
        </Card.Body>
      </Card>

      <style>{`
        .admin-user-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 24px 0;
        }

        .admin-user-header-title {
          min-width: 0;
        }

        .admin-user-header-actions {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex-wrap: wrap;
        }

        .admin-user-header-actions .btn {
          white-space: nowrap;
          flex: 0 0 auto;
        }

        .user-detail-shell {
          background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
        }

        .profile-summary-card {
          background: linear-gradient(180deg, rgba(44, 96, 186, 0.06) 0%, #ffffff 100%);
        }

        .profile-top {
          text-align: center;
        }

        .profile-avatar-wrap {
          width: 132px;
          height: 132px;
          margin: 0 auto;
          position: relative;
        }

        .profile-avatar,
        .profile-avatar-fallback {
          width: 132px;
          height: 132px;
          border-radius: 50%;
          object-fit: cover;
          border: 5px solid #ffffff;
          box-shadow: 0 10px 24px rgba(16, 24, 40, 0.12);
        }

        .profile-avatar-fallback {
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f4f6f8;
          color: #8d98a9;
          font-size: 3rem;
        }

        .profile-mini-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
          text-align: left;
        }

        .profile-mini-item {
          border: 1px solid #edf0f5;
          border-radius: 14px;
          padding: 10px 12px;
          background: #fff;
        }

        .detail-box {
          border: 1px solid #edf0f5;
          border-radius: 14px;
          padding: 12px 14px;
          background: #fff;
          height: 100%;
        }

        .media-preview-card {
          border: 1px solid #edf0f5;
          border-radius: 18px;
          padding: 16px;
          background: #fff;
          height: 100%;
        }

        .media-preview-thumb {
          width: 100%;
          aspect-ratio: 16 / 10;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid #edf0f5;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .media-preview-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .media-preview-file {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          color: #005dfb;
          background: rgba(44, 96, 186, 0.08);
        }

        .media-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 220px;
          text-align: center;
        }

        .view-btn {
          white-space: nowrap;
        }

        @media (max-width: 767.98px) {
          .admin-user-header {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            flex-wrap: wrap;
          }

          .admin-user-header-title {
            width: 100%;
          }

          .admin-user-header-actions {
            width: 100%;
            justify-content: flex-end;
            gap: 8px;
          }

          .admin-user-header-actions .btn {
            width: auto;
            flex: 0 0 auto;
          }

          .profile-mini-grid {
            grid-template-columns: 1fr;
          }

          .profile-avatar-wrap {
            width: 108px;
            height: 108px;
          }

          .profile-avatar,
          .profile-avatar-fallback {
            width: 108px;
            height: 108px;
          }

          .media-preview-card {
            padding: 14px;
          }

          .detail-box {
            padding: 11px 12px;
          }

          .table th,
          .table td {
            white-space: nowrap;
          }
        }
      `}</style>
    </>
  );
}